import { Leaf, Recycle, Sun, Droplet, Home, Award } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";

const Sustainability = () => {
  const { t } = useTranslation();

  const sustainabilityPractices = [
    {
      id: "waste-reduction",
      icon: Recycle,
      title: "Waste Reduction & Recycling",
      description: "We minimize construction waste and recycle materials whenever possible. Our team carefully sorts debris and partners with local recycling centers to ensure responsible disposal.",
      color: "bg-green-100 text-green-600"
    },
    {
      id: "energy-efficient",
      icon: Sun,
      title: "Energy-Efficient Solutions",
      description: "We recommend and install energy-efficient systems including LED lighting, smart thermostats, and high-efficiency appliances to reduce your carbon footprint and energy bills.",
      color: "bg-yellow-100 text-yellow-600"
    },
    {
      id: "water-conservation",
      icon: Droplet,
      title: "Water Conservation",
      description: "Installing low-flow fixtures, water-efficient faucets, and leak-prevention systems help conserve water without compromising performance or comfort.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      id: "sustainable-materials",
      icon: Home,
      title: "Sustainable Materials",
      description: "We source eco-friendly, sustainable materials including FSC-certified wood, low-VOC paints, and recycled content products that are better for your health and the environment.",
      color: "bg-[#1a7a8a] 100 text-[#156a78]"
    },
    {
      id: "green-building",
      icon: Leaf,
      title: "Green Building Practices",
      description: "Our renovation methods follow green building standards, focusing on improving insulation, air quality, and overall energy efficiency of your home.",
      color: "bg-emerald-100 text-emerald-600"
    },
    {
      id: "long-lasting-quality",
      icon: Award,
      title: "Long-Lasting Quality",
      description: "We prioritize durable, high-quality workmanship that stands the test of time, reducing the need for frequent renovations and minimizing long-term environmental impact.",
      color: "bg-purple-100 text-purple-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6">
              <Leaf className="text-white" size={40} />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our Commitment to{" "}
              <span className="text-green-600">Sustainability</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Building a better future through responsible renovation practices. We believe in creating beautiful spaces while protecting our planet for future generations.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How We Make a Difference
            </h2>
            <p className="text-lg text-gray-600">
              At Basic Job, sustainability isn't just a buzzword—it's woven into every project we undertake. From material selection to waste management, we're committed to environmentally responsible practices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {sustainabilityPractices.map((practice) => {
              const Icon = practice.icon;
              return (
                <div
                  key={practice.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-t-4 border-green-500"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`${practice.color} p-4 rounded-full flex-shrink-0`}>
                      <Icon size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {practice.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {practice.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready for a Sustainable Renovation?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let's create a beautiful, eco-friendly space together. Contact us today to discuss your sustainable renovation project.
            </p>
            <a
              href="/"
              className="inline-block bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get Your Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Sustainability;
